import { PUBLIC_BACKEND } from '$app/env/public';
import { getMarketplaceSeoKey, type MarketplaceSeoKey } from '$lib/content/marketplace-seo';
import { barcosRoutePbFilter, collectBarcosPaths, parseBarcosSegments, slugify } from '$lib/geo';
import { minAvailableOrLatestRangePrice } from '$lib/listing-pricing/calculate';
import type {
	BoatsResponse,
	ListingDateRangesResponse,
	ListingsResponse,
	PortsResponse,
	ReviewsResponse,
	UsersResponse
} from '@/pocketbase/pocketbase-types';
import { PortsProvinceOptions } from '@/pocketbase/pocketbase-types';
import { error } from '@sveltejs/kit';
import type { EntryGenerator, PageLoad } from './$types';

export interface ListingDetailExpand extends ListingsResponse {
	expand?: {
		boat?: BoatsResponse & { expand?: { port?: PortsResponse; user?: UsersResponse } };
		user?: UsersResponse;
		cancellationPolicy?: { name: string };
	};
}

export type RelatedListing = ListingDetailExpand & {
	minPriceFromRanges?: number | null;
};

export type FeaturedReview = ReviewsResponse<{
	reviewer: UsersResponse;
	listing: ListingsResponse & { expand?: { boat?: { expand?: { port?: PortsResponse } } } };
}>;

import type { ListingDetailReview } from '$lib/listing-detail/review-types';

const MAX_RELATED_LISTINGS = 4;
const MIN_FEATURED_REVIEWS = 3;
const TARGET_FEATURED_REVIEWS = 6;

const GALICIA_PROVINCE_FILTER = [
	PortsProvinceOptions.Pontevedra,
	PortsProvinceOptions['A Coruña'],
	PortsProvinceOptions.Lugo
]
	.map((province) => `listing.boat.port.province = "${province}"`)
	.join(' || ');

async function fetchListingReviews(
	fetchFn: typeof fetch,
	listingId: string
): Promise<ListingDetailReview[]> {
	const query = new URLSearchParams({
		filter: `listing = "${listingId}"`,
		expand: 'reviewer',
		sort: '-created',
		perPage: '50'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/reviews/records?${query}`);
	if (!res.ok) return [];
	const data = (await res.json()) as { items: ListingDetailReview[] };
	return data.items;
}

async function fetchOwnerReviewCount(fetchFn: typeof fetch, ownerId: string): Promise<number> {
	const query = new URLSearchParams({
		filter: `reviewee = "${ownerId}"`,
		perPage: '1',
		fields: 'id'
	});
	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/reviews/records?${query}`);
	if (!res.ok) return 0;
	const data = (await res.json()) as { totalItems?: number };
	return data.totalItems ?? 0;
}

async function fetchFiveStarReviews(
	fetchFn: typeof fetch,
	geoFilter: string | null,
	limit: number
): Promise<FeaturedReview[]> {
	const filters = ['rating = 5', 'comment != ""'];
	if (geoFilter) filters.push(`(${geoFilter})`);

	const query = new URLSearchParams({
		filter: filters.join(' && '),
		expand: 'reviewer,listing.boat.port',
		sort: '-created',
		perPage: String(limit)
	});

	const res = await fetchFn(`${PUBLIC_BACKEND}/api/collections/reviews/records?${query}`);
	if (!res.ok) return [];
	const data = (await res.json()) as { items: FeaturedReview[] };
	return data.items;
}

async function loadFeaturedReviews(
	fetchFn: typeof fetch,
	seoKey: MarketplaceSeoKey
): Promise<FeaturedReview[]> {
	const geoFilter = seoKey === 'galicia' ? GALICIA_PROVINCE_FILTER : null;

	let reviews = await fetchFiveStarReviews(fetchFn, geoFilter, TARGET_FEATURED_REVIEWS);
	if (reviews.length < MIN_FEATURED_REVIEWS && geoFilter) {
		reviews = await fetchFiveStarReviews(fetchFn, null, TARGET_FEATURED_REVIEWS);
	}

	return reviews.slice(0, TARGET_FEATURED_REVIEWS);
}

export const load: PageLoad = async ({ params, fetch }) => {
	const segments = params.segments ? params.segments.split('/').filter(Boolean) : [];
	const route = parseBarcosSegments(segments);
	if (!route) {
		error(404, 'Página no encontrada');
	}

	if (route.listingId) {
		const res = await fetch(
			`${PUBLIC_BACKEND}/api/collections/listings/records/${route.listingId}?expand=user,boat.port,cancellationPolicy`
		);
		if (!res.ok) {
			error(404, 'Anuncio no encontrado');
		}
		const listing = (await res.json()) as ListingDetailExpand;
		const ownerId = listing.expand?.user?.id;
		const [relatedListings, ownerReviewCount, listingReviews] = await Promise.all([
			loadRelatedListings(fetch, listing),
			ownerId ? fetchOwnerReviewCount(fetch, ownerId) : Promise.resolve(0),
			fetchListingReviews(fetch, route.listingId)
		]);
		return {
			mode: 'detail' as const,
			route,
			listing,
			relatedListings,
			ownerReviewCount,
			listingReviews,
			cityName: null,
			portId: null,
			portName: null,
			marketplaceSeoKey: null,
			featuredReviews: [] as FeaturedReview[]
		};
	}

	// Modo filtro: resolver los slugs de ciudad/puerto contra la colección de
	// puertos de la provincia.
	let cityName: string | null = null;
	let portId: string | null = null;
	let portName: string | null = null;
	if (route.ciudadSlug && route.provincia) {
		const query = new URLSearchParams({
			perPage: '500',
			filter: `province = "${route.provincia}" && city != ""`,
			fields: 'id,name,city'
		});
		const res = await fetch(`${PUBLIC_BACKEND}/api/collections/ports/records?${query}`);
		if (res.ok) {
			const data = (await res.json()) as { items: { id: string; name: string; city: string }[] };
			const cityPorts = data.items.filter((p) => slugify(p.city) === route.ciudadSlug);
			cityName = cityPorts[0]?.city ?? null;
			if (route.puertoSlug) {
				const port = cityPorts.find((p) => slugify(p.name) === route.puertoSlug);
				portId = port?.id ?? null;
				portName = port?.name.trim() ?? null;
			}
		}
		if (!cityName || (route.puertoSlug && !portId)) {
			error(404, 'Ubicación no encontrada');
		}
	}

	// Primera página de resultados para que el HTML prerenderizado contenga
	// los anuncios (contenido + enlaces internos al detalle).
	let initialListings: ListingDetailExpand[] = [];
	let initialTotalItems = 0;
	let initialTotalPages = 0;
	try {
		const query = new URLSearchParams({
			page: '1',
			perPage: '16',
			sort: '-created',
			expand: 'boat.port',
			filter: barcosRoutePbFilter(route, cityName, portId)
		});
		const res = await fetch(`${PUBLIC_BACKEND}/api/collections/listings/records?${query}`);
		if (res.ok) {
			const data = await res.json();
			initialListings = data.items;
			initialTotalItems = data.totalItems;
			initialTotalPages = data.totalPages;
		}
	} catch (err) {
		console.error('[barcos] no se pudo cargar la primera página de anuncios:', err);
	}

	const marketplaceSeoKey = getMarketplaceSeoKey(route);
	let featuredReviews: FeaturedReview[] = [];
	if (marketplaceSeoKey) {
		try {
			featuredReviews = await loadFeaturedReviews(fetch, marketplaceSeoKey);
		} catch (err) {
			console.error('[barcos] no se pudieron cargar reseñas destacadas:', err);
		}
	}

	return {
		mode: 'market' as const,
		route,
		listing: null,
		relatedListings: [] as RelatedListing[],
		cityName,
		portId,
		portName,
		initialListings,
		initialTotalItems,
		initialTotalPages,
		marketplaceSeoKey,
		featuredReviews
	};
};

async function fetchRelatedCandidates(
	fetch: Parameters<PageLoad>[0]['fetch'],
	filter: string,
	limit: number
): Promise<ListingDetailExpand[]> {
	const query = new URLSearchParams({
		page: '1',
		perPage: String(Math.min(limit, 50)),
		sort: '-created',
		expand: 'boat.port',
		filter
	});
	const res = await fetch(`${PUBLIC_BACKEND}/api/collections/listings/records?${query}`);
	if (!res.ok) return [];
	const data = (await res.json()) as { items: ListingDetailExpand[] };
	return data.items;
}

async function attachMinPrices(
	fetch: Parameters<PageLoad>[0]['fetch'],
	listings: ListingDetailExpand[]
): Promise<RelatedListing[]> {
	if (listings.length === 0) return [];

	const ids = listings.map((listing) => listing.id);
	const rangesFilter = ids.map((id) => `listing = "${id}"`).join(' || ');
	const query = new URLSearchParams({
		perPage: '500',
		filter: `(${rangesFilter})`
	});

	let ranges: ListingDateRangesResponse[] = [];
	try {
		const res = await fetch(`${PUBLIC_BACKEND}/api/collections/listingDateRanges/records?${query}`);
		if (res.ok) {
			const data = await res.json();
			ranges = data.items;
		}
	} catch (err) {
		console.error('[barcos] no se pudieron cargar rangos de precio relacionados:', err);
	}

	const rangesByListing = new Map<string, ListingDateRangesResponse[]>();
	for (const range of ranges) {
		const listingRanges = rangesByListing.get(range.listing) ?? [];
		listingRanges.push(range);
		rangesByListing.set(range.listing, listingRanges);
	}

	return listings.map((listing) => ({
		...listing,
		minPriceFromRanges: minAvailableOrLatestRangePrice(rangesByListing.get(listing.id) ?? [])
	}));
}

async function loadRelatedListings(
	fetch: Parameters<PageLoad>[0]['fetch'],
	listing: ListingDetailExpand
): Promise<RelatedListing[]> {
	const excludeId = listing.id;
	const port = listing.expand?.boat?.expand?.port;
	const portId = port?.id ?? listing.expand?.boat?.port;
	const base = `status = "active" && id != "${excludeId}"`;

	const collected: ListingDetailExpand[] = [];
	const seen = new Set<string>([excludeId]);

	const addTier = async (locationClause: string | null) => {
		if (collected.length >= MAX_RELATED_LISTINGS) return;

		const filter = locationClause ? `${base} && ${locationClause}` : base;
		const needed = MAX_RELATED_LISTINGS - collected.length;
		const candidates = await fetchRelatedCandidates(fetch, filter, needed + 4);

		for (const candidate of candidates) {
			if (seen.has(candidate.id)) continue;
			seen.add(candidate.id);
			collected.push(candidate);
			if (collected.length >= MAX_RELATED_LISTINGS) break;
		}
	};

	if (portId) {
		await addTier(`boat.port = "${portId}"`);
	}

	if (port?.city && port?.province) {
		await addTier(`boat.port.city = "${port.city}" && boat.port.province = "${port.province}"`);
	} else if (port?.province) {
		await addTier(`boat.port.province = "${port.province}"`);
	}

	if (collected.length < MAX_RELATED_LISTINGS) {
		await addTier(null);
	}

	return attachMinPrices(fetch, collected);
}

/**
 * Entradas de prerender: se generan a partir de los listings activos en
 * PocketBase (como hace el sitemap), de modo que solo se prerenderizan
 * páginas de filtro con al menos un anuncio, más el detalle de cada listing.
 * Si PB no está accesible durante el build, degrada a solo /barcos.
 */
export const entries: EntryGenerator = async () => {
	let paths = ['/barcos', '/barcos/alquiler', '/barcos/experiencia'];

	try {
		const listings = await fetchActiveListings();
		paths = collectBarcosPaths(listings);
	} catch (err) {
		console.error('[barcos] entries: no se pudieron cargar los listings:', err);
	}

	return paths.map((path) => ({ segments: path.replace(/^\/barcos\/?/, '') }));
};

async function fetchActiveListings(): Promise<ListingDetailExpand[]> {
	const all: ListingDetailExpand[] = [];
	let page = 1;
	let totalPages = 1;

	while (page <= totalPages) {
		const query = new URLSearchParams({
			filter: `status = 'active'`,
			expand: 'boat.port',
			perPage: '200',
			page: String(page)
		});
		const res = await fetch(`${PUBLIC_BACKEND}/api/collections/listings/records?${query}`);
		if (!res.ok) {
			throw new Error(`PocketBase respondió ${res.status}`);
		}
		const data = await res.json();
		all.push(...data.items);
		totalPages = data.totalPages;
		page++;
	}

	return all;
}
